import "./QuizQuestionComponent.scss";
import {AnswerComponentController} from "../answer/AnswerComponent";
import {IFormController, IPostLink} from "angular";
import {QuizQuestionAnswer} from "../../models/QuizQuestionAnswer";
import {ComponentCallback} from "../../../../types/AngularJS";
import {Optional} from "../../../../js/util/Optional";

export const QuizQuestionComponent = "tfQuizQuestion";

export function quizQuestionComponent() {
    return {
        templateUrl: "app/components/quiz/components/question/QuizQuestionComponent.html",
        transclude: {
            titleSlot: "?tfQuestionTitle",
            questionSlot: "tfQuestion",
            answerSlot: "tfAnswer"
        },
        bindings: {
            name: "<",
            previousAnswer: "<",
            minAnswers: "<",
            maxAnswers: "<",
            onAnswer: "&"
        },
        controllerAs: "ctrl",
        controller: QuizQuestionComponentController
    };
}

export class QuizQuestionComponentController implements IPostLink {

    private static VALIDITY_KEY = "question-answered";

    public name: string;
    public previousAnswer?: Array<string>;
    public minAnswers: number;
    public maxAnswers: number;
    public onAnswer: ComponentCallback;

    public questionForm: IFormController;

    private readonly answers: Array<AnswerComponentController> = [];
    private selected: Array<AnswerComponentController> = [];

    public $postLink(): void {
        this.minAnswers = Optional.of(this.minAnswers).orElse(1);
        this.maxAnswers = Optional.of(this.maxAnswers).orElse(1);
        if (this.maxAnswers < this.minAnswers) {
            throw Error("Cannot have a Max Selected that is less than a Min Selected");
        }
        this.checkAnswerState();
    }

    public register(answer: AnswerComponentController) {
        this.answers.push(answer);
        if (this.previousAnswer != null) {
            if (this.previousAnswer.includes(answer.name)) {
                this.select(answer);
            }
        }
    }

    public select(answer: AnswerComponentController) {
        answer.selected = true;
        if (this.isMulti) {
            this.multiSelect(answer);
        } else {
            this.selectSingle(answer);
        }
        this.checkAnswerState();
    }

    private selectSingle(answer: AnswerComponentController) {
        if (this.selected.length === 0) {
            this.selected.push(answer);
        } else {
            const current = this.selected.pop()!;
            current.selected = false;
            this.selected.push(answer);
        }
    }

    private multiSelect(answer: AnswerComponentController) {
        this.selected.push(answer);
        // Only disable the other answers for the complete multi-select as they act like a set of checkboxes
        // rather than a set of radio buttons
        if (this.selected.length === this.maxAnswers) {
            this.unselected().forEach(a => a.disabled = true);
        }
    }

    private checkAnswerState() {
        this.isAnswered = this.maxAnswers === this.selected.length;
    }

    private set isAnswered(answered: boolean) {
        this.questionForm.$setValidity(QuizQuestionComponentController.VALIDITY_KEY, answered, this.questionForm);
        if (answered) {
            const result = this.buildAnswer();
            this.onAnswer({answer: result});
        }
    }

    private buildAnswer(): QuizQuestionAnswer {
        const answers = this.selected.map(a => a.name);
        return {
            question: this.name,
            answers
        };
    }

    public deselect(answer: AnswerComponentController) {
        answer.selected = false;
        this.selected = this.selected.filter(a => a.name !== answer.name);
        if (this.isMulti) {
            this.unselected().forEach(a => a.disabled = false);
        }
        this.checkAnswerState();
    }

    private unselected(): Array<AnswerComponentController> {
        const selectedNames = this.selected.map(a => a.name);
        return this.answers.filter(a => !selectedNames.includes(a.name));
    }

    private get isMulti(): boolean {
        return this.maxAnswers > 1;
    }
}
